import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, MapPin, DollarSign, MessageCircle, CheckCircle, XCircle, Clock, User,
  CheckSquare, Camera, AlertTriangle, ImagePlus, Trash2, Star,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BrickRatingInput } from '@/components/BrickRating';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const buildAuthUrl = (url, token) => {
  if (!url) return url;
  if (url.startsWith('http')) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${BACKEND_URL}${url}${sep}auth=${token}`;
};

export default function JobDetails({ user, token }) {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const [proposedPrice, setProposedPrice] = useState('');
  const [proposingPrice, setProposingPrice] = useState(false);

  // Completion flow
  const [showFinishDialog, setShowFinishDialog] = useState(false);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [evidenceComment, setEvidenceComment] = useState('');
  const [submittingEvidence, setSubmittingEvidence] = useState(false);
  const evidenceInputRef = useRef(null);

  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [confirmingCompletion, setConfirmingCompletion] = useState(false);

  // Rating flow
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  // Client-driven completion
  const [showClientFinishDialog, setShowClientFinishDialog] = useState(false);
  const [clientEvidenceFiles, setClientEvidenceFiles] = useState([]);
  const [clientEvidenceComment, setClientEvidenceComment] = useState('');
  const [submittingClientFinish, setSubmittingClientFinish] = useState(false);
  const clientEvidenceInputRef = useRef(null);

  useEffect(() => {
    fetchJobDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/jobs/${jobId}`, config);
      setJob(response.data);
      if (response.data.budget) setProposedPrice(response.data.budget.toString());
    } catch (error) {
      toast.error('Error al cargar trabajo');
    } finally {
      setLoading(false);
    }
  };

  const handleProposePrice = async () => {
    if (!proposedPrice || parseFloat(proposedPrice) <= 0) {
      toast.error('Ingresa un precio válido');
      return;
    }
    setProposingPrice(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/jobs/${jobId}/propose-price?proposed_price=${proposedPrice}`, {}, config);
      toast.success('¡Propuesta enviada! El cliente revisará tu oferta.');
      setShowPriceDialog(false);
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al proponer precio');
    } finally {
      setProposingPrice(false);
    }
  };

  const handleAcceptPrice = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.put(`${API}/jobs/${jobId}/accept-price`, {}, config);
      toast.success('¡Trato cerrado! Ya puedes chatear con el trabajador.');
      if (response.data.conversation_id) navigate(`/chat/${response.data.conversation_id}`);
      else fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar precio');
    }
  };

  const handleRejectPrice = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/jobs/${jobId}/reject-price`, {}, config);
      toast.success('Propuesta rechazada. El trabajo está disponible nuevamente.');
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar precio');
    }
  };

  const handleGoToChat = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/jobs/${jobId}/conversation`, config);
      navigate(`/chat/${response.data.conversation_id}`);
    } catch (error) {
      toast.error('No hay conversación disponible aún');
    }
  };

  // ===== Completion flow =====
  const handleEvidenceSelect = (e) => {
    const list = Array.from(e.target.files || []);
    const merged = [...evidenceFiles, ...list].slice(0, 3);
    setEvidenceFiles(merged);
    if (evidenceInputRef.current) evidenceInputRef.current.value = '';
  };

  const removeEvidence = (idx) => {
    setEvidenceFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmitEvidence = async () => {
    if (evidenceFiles.length < 2) {
      toast.error('Debes subir al menos 2 fotos de evidencia');
      return;
    }
    setSubmittingEvidence(true);
    try {
      const formData = new FormData();
      evidenceFiles.forEach((f) => formData.append('files', f));
      const url = evidenceComment.trim()
        ? `${API}/jobs/${jobId}/mark-completed?comment=${encodeURIComponent(evidenceComment.trim())}`
        : `${API}/jobs/${jobId}/mark-completed`;
      await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('¡Evidencia enviada! El cliente confirmará la finalización.');
      setShowFinishDialog(false);
      setEvidenceFiles([]);
      setEvidenceComment('');
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar evidencia');
    } finally {
      setSubmittingEvidence(false);
    }
  };

  const handleConfirmCompletion = async () => {
    setConfirmingCompletion(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.put(`${API}/jobs/${jobId}/confirm-completion`, {}, config);
      toast.success(`¡Trabajo finalizado! Pago liberado al trabajador. ${res.data.cashback > 0 ? `Cashback: $${res.data.cashback.toLocaleString()}` : ''}`);
      await fetchJobDetails();
      // Open rating dialog automatically
      setShowRatingDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar');
    } finally {
      setConfirmingCompletion(false);
    }
  };

  const handleSubmitRating = async () => {
    if (ratingValue === 0) {
      toast.error('Selecciona al menos 1 ladrillito');
      return;
    }
    setSubmittingRating(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/reviews`, {
        job_id: jobId,
        worker_id: job.worker_id,
        rating: ratingValue,
        comment: ratingComment.trim(),
      }, config);
      toast.success('¡Gracias por tu calificación! 🧱');
      setRatingSubmitted(true);
      setShowRatingDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar calificación');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleReportIssue = async () => {
    if (!disputeReason.trim() || disputeReason.trim().length < 10) {
      toast.error('Explica brevemente el problema (mínimo 10 caracteres)');
      return;
    }
    setSubmittingDispute(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(
        `${API}/jobs/${jobId}/report-issue?reason=${encodeURIComponent(disputeReason.trim())}`,
        {},
        config
      );
      toast.success('Reporte enviado. Un administrador revisará el caso.');
      setShowDisputeDialog(false);
      setDisputeReason('');
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reportar');
    } finally {
      setSubmittingDispute(false);
    }
  };

  // Client-driven completion handlers
  const handleClientEvidenceSelect = (e) => {
    const list = Array.from(e.target.files || []);
    const merged = [...clientEvidenceFiles, ...list].slice(0, 3);
    setClientEvidenceFiles(merged);
    if (clientEvidenceInputRef.current) clientEvidenceInputRef.current.value = '';
  };

  const removeClientEvidence = (idx) => {
    setClientEvidenceFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleClientFinish = async () => {
    setSubmittingClientFinish(true);
    try {
      const formData = new FormData();
      clientEvidenceFiles.forEach((f) => formData.append('files', f));
      const url = clientEvidenceComment.trim()
        ? `${API}/jobs/${jobId}/client-mark-completed?comment=${encodeURIComponent(clientEvidenceComment.trim())}`
        : `${API}/jobs/${jobId}/client-mark-completed`;
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.payment_executed) {
        toast.success(`¡Trabajo cerrado! Pago liberado al trabajador. ${res.data.cashback > 0 ? `Cashback: $${res.data.cashback.toLocaleString()}` : ''}`);
      } else {
        toast.success('¡Trabajo marcado como terminado!');
      }
      setShowClientFinishDialog(false);
      setClientEvidenceFiles([]);
      setClientEvidenceComment('');
      await fetchJobDetails();
      // Open rating only if there was a contracted worker
      if (res.data.payment_executed) setShowRatingDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar el trabajo');
    } finally {
      setSubmittingClientFinish(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Cargando...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Trabajo no encontrado</p>
      </div>
    );
  }

  const isClient = job.client_id === user.id;
  const isWorker = !isClient;
  const isMyProposal = job.proposed_worker_id === user.id;
  const isAssignedWorker = job.worker_id === user.id;

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendiente' },
      proposed: { color: 'bg-blue-100 text-blue-800', label: 'Propuesta enviada' },
      accepted: { color: 'bg-green-100 text-green-800', label: 'Aceptado' },
      in_progress: { color: 'bg-purple-100 text-purple-800', label: 'En Progreso' },
      awaiting_client_confirmation: { color: 'bg-amber-100 text-amber-800', label: 'Esperando confirmación' },
      disputed: { color: 'bg-red-100 text-red-800', label: 'En disputa' },
      completed: { color: 'bg-green-100 text-green-800', label: 'Completado' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelado' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    const config = {
      normal: { color: 'bg-slate-100 text-slate-700', label: 'Normal' },
      urgent: { color: 'bg-yellow-100 text-yellow-700', label: 'Urgente' },
      emergency: { color: 'bg-red-100 text-red-700', label: 'Emergencia' },
    };
    const cfg = config[urgency] || config.normal;
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  const categoryLabels = {
    plomeria: 'Plomería', electricidad: 'Electricidad', pintura: 'Pintura',
    albanileria: 'Albañilería', ingeniero_civil: 'Ingeniero Civil',
    arquitecto: 'Arquitecto', topografia: 'Topografía', cerrajeria: 'Cerrajería',
    planos_disenos: 'Planos y Diseños', licencias: 'Licencias',
  };

  const evidenceImages = job.evidence_images || [];
  const canWorkerMarkCompleted = isWorker && isAssignedWorker && ['accepted', 'in_progress'].includes(job.status);
  const clientAwaitingConfirmation = isClient && job.status === 'awaiting_client_confirmation';
  const workerWaitingConfirmation = isWorker && isAssignedWorker && job.status === 'awaiting_client_confirmation';
  const canClientMarkCompleted = isClient && ['pending', 'proposed', 'accepted', 'in_progress'].includes(job.status);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3A4F6C] to-slate-800 text-white p-6 sticky top-0 z-40">
        <div className="container mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="text-white hover:text-orange-400 mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-6 w-6" />
            Volver
          </button>
          <h1 className="text-2xl font-bold" data-testid="job-title">{job.title}</h1>
          <div className="flex gap-2 mt-2 flex-wrap">
            {getStatusBadge(job.status)}
            {getUrgencyBadge(job.urgency)}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Price Proposal Banner - Client */}
        {isClient && job.status === 'proposed' && (
          <Card className="mb-6 border-2 border-orange-400 bg-orange-50" data-testid="price-proposal-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <DollarSign className="h-8 w-8 text-orange-600" />
                <div>
                  <h3 className="font-bold text-lg text-slate-800">¡Tienes una propuesta!</h3>
                  <p className="text-slate-600">Un trabajador ha propuesto un precio para tu trabajo</p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-500 mb-1">Precio propuesto:</p>
                <p className="text-3xl font-bold text-orange-600">${job.proposed_price?.toLocaleString()} COP</p>
              </div>
              <div className="flex gap-3">
                <Button
                  data-testid="accept-price-button"
                  onClick={handleAcceptPrice}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Aceptar y Chatear
                </Button>
                <Button
                  data-testid="reject-price-button"
                  onClick={handleRejectPrice}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="h-5 w-5 mr-2" />
                  Rechazar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Waiting proposal response - Worker */}
        {isWorker && job.status === 'proposed' && isMyProposal && (
          <Card className="mb-6 border-2 border-blue-300 bg-blue-50">
            <CardContent className="p-6 text-center">
              <Clock className="h-12 w-12 text-blue-500 mx-auto mb-3" />
              <h3 className="font-bold text-lg text-slate-800 mb-2">Propuesta enviada</h3>
              <p className="text-slate-600 mb-2">
                Propusiste <strong className="text-orange-600">${job.proposed_price?.toLocaleString()}</strong> para este trabajo.
              </p>
              <p className="text-sm text-slate-500">Esperando respuesta del cliente...</p>
            </CardContent>
          </Card>
        )}

        {/* Chat + Finish available */}
        {job.status === 'accepted' && (
          <Card className="mb-6 border-2 border-green-400 bg-green-50">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-8 w-8 text-green-600 shrink-0" />
                  <div>
                    <h3 className="font-bold text-lg text-slate-800">¡Trato cerrado!</h3>
                    <p className="text-slate-600">
                      Precio acordado: <strong className="text-orange-600">${job.accepted_price?.toLocaleString()}</strong>
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="go-to-chat-button"
                  onClick={handleGoToChat}
                  className="bg-orange-500 hover:bg-orange-600 text-white w-full sm:w-auto"
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Ir al Chat
                </Button>
              </div>

              {canWorkerMarkCompleted && (
                <div className="mt-4 pt-4 border-t border-green-300">
                  <Button
                    data-testid="mark-completed-button"
                    onClick={() => setShowFinishDialog(true)}
                    className="w-full bg-[#3A4F6C] hover:bg-[#2A3B4F] text-white font-bold py-3"
                  >
                    <CheckSquare className="h-5 w-5 mr-2" />
                    Marcar Trabajo Terminado
                  </Button>
                  <p className="text-xs text-slate-500 text-center mt-2">
                    Sube 2-3 fotos como evidencia del trabajo finalizado
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Awaiting Confirmation - Worker view */}
        {workerWaitingConfirmation && (
          <Card className="mb-6 border-2 border-amber-400 bg-amber-50" data-testid="worker-awaiting-card">
            <CardContent className="p-6 text-center">
              <Clock className="h-12 w-12 text-amber-600 mx-auto mb-3" />
              <h3 className="font-bold text-lg text-slate-800 mb-2">Evidencia enviada</h3>
              <p className="text-slate-600 mb-1">
                Enviaste {evidenceImages.length} foto(s) de evidencia. Esperando confirmación del cliente para liberar el pago.
              </p>
              {job.evidence_comment && (
                <p className="text-sm text-slate-500 italic mt-2">"{job.evidence_comment}"</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Awaiting Confirmation - Client view */}
        {clientAwaitingConfirmation && (
          <Card className="mb-6 border-2 border-amber-400 bg-amber-50" data-testid="client-confirm-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Camera className="h-8 w-8 text-amber-600 shrink-0" />
                <div>
                  <h3 className="font-bold text-lg text-slate-800">El trabajador terminó el trabajo</h3>
                  <p className="text-slate-600">Revisa la evidencia y confirma la finalización</p>
                </div>
              </div>

              {evidenceImages.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  {evidenceImages.map((url, i) => (
                    <img
                      key={i}
                      src={buildAuthUrl(url, token)}
                      alt={`Evidencia ${i + 1}`}
                      onClick={() => window.open(buildAuthUrl(url, token), '_blank')}
                      className="w-full h-40 object-cover rounded-lg border-2 border-amber-200 hover:border-orange-500 cursor-pointer transition-colors"
                      data-testid={`evidence-image-${i}`}
                    />
                  ))}
                </div>
              )}

              {job.evidence_comment && (
                <div className="bg-white rounded-lg p-3 mb-4 border border-amber-200">
                  <p className="text-xs text-slate-500 mb-1">Comentario del trabajador:</p>
                  <p className="text-slate-700 italic">"{job.evidence_comment}"</p>
                </div>
              )}

              <div className="bg-white rounded-lg p-3 mb-4 border border-slate-200">
                <p className="text-sm text-slate-600">
                  Monto a pagar: <strong className="text-orange-600">${(job.accepted_price || 0).toLocaleString()} COP</strong>
                </p>
                <p className="text-xs text-slate-500 mt-1">Se descontará automáticamente de tu billetera al confirmar</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="confirm-completion-button"
                  onClick={handleConfirmCompletion}
                  disabled={confirmingCompletion}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  {confirmingCompletion ? 'Procesando...' : 'Confirmar y Finalizar'}
                </Button>
                <Button
                  data-testid="report-issue-button"
                  onClick={() => setShowDisputeDialog(true)}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                >
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Reportar Problema
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Disputed banner */}
        {job.status === 'disputed' && (
          <Card className="mb-6 border-2 border-red-400 bg-red-50">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-3" />
              <h3 className="font-bold text-lg text-slate-800 mb-2">Trabajo en disputa</h3>
              <p className="text-slate-600">Un administrador revisará el caso y se pondrá en contacto.</p>
              {job.dispute_reason && (
                <p className="text-sm text-slate-500 mt-3 italic">"{job.dispute_reason}"</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Completed banner */}
        {job.status === 'completed' && (
          <Card className="mb-6 border-2 border-green-500 bg-green-50">
            <CardContent className="p-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <h3 className="font-bold text-lg text-slate-800 mb-2">¡Trabajo finalizado!</h3>
              <p className="text-slate-600">
                Monto pagado: <strong className="text-orange-600">${(job.final_price || job.accepted_price || 0).toLocaleString()} COP</strong>
              </p>

              {evidenceImages.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                  {evidenceImages.map((url, i) => (
                    <img
                      key={i}
                      src={buildAuthUrl(url, token)}
                      alt={`Evidencia ${i + 1}`}
                      onClick={() => window.open(buildAuthUrl(url, token), '_blank')}
                      className="w-full h-32 object-cover rounded-lg border cursor-pointer"
                    />
                  ))}
                </div>
              )}

              {isClient && !ratingSubmitted && (
                <Button
                  data-testid="open-rating-button"
                  onClick={() => setShowRatingDialog(true)}
                  className="mt-4 bg-orange-500 hover:bg-orange-600 text-white font-bold"
                >
                  <Star className="h-5 w-5 mr-2" />
                  Calificar al trabajador
                </Button>
              )}
              {isClient && ratingSubmitted && (
                <p className="text-sm text-green-700 mt-4 flex items-center justify-center gap-2">
                  <CheckCircle className="h-4 w-4" /> Calificación enviada
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Job Card */}
        <Card className="mb-6" data-testid="job-details-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-slate-500" />
              Detalles del Trabajo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-slate-700 mb-1">Descripción</h3>
              <p className="text-slate-600">{job.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-slate-700 mb-1">Categoría</h3>
                <Badge className="bg-orange-100 text-orange-700">
                  {categoryLabels[job.category] || job.category}
                </Badge>
              </div>
              <div>
                <h3 className="font-semibold text-slate-700 mb-1">Presupuesto del cliente</h3>
                <p className="text-xl font-bold text-slate-700">${job.budget?.toLocaleString()} COP</p>
              </div>
            </div>

            {job.location?.address && (
              <div className="flex items-start gap-2">
                <MapPin className="h-5 w-5 text-orange-500 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-slate-700 mb-1">Ubicación</h3>
                  <p className="text-slate-600">{job.location.address}</p>
                </div>
              </div>
            )}

            {job.images && job.images.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-700 mb-3">Fotos del Problema</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {job.images.map((image, index) => (
                    <div key={index} className="relative group cursor-pointer">
                      <img
                        src={image}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg border-2 border-slate-200 hover:border-orange-500 transition-colors"
                        onClick={() => window.open(image, '_blank')}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isWorker && job.status === 'pending' && (
              <Button
                data-testid="propose-price-button"
                onClick={() => setShowPriceDialog(true)}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-full"
              >
                <DollarSign className="h-5 w-5 mr-2" />
                Proponer Precio
              </Button>
            )}

            {canClientMarkCompleted && (
              <div className="pt-2 border-t border-slate-200">
                <Button
                  data-testid="client-mark-completed-button"
                  onClick={() => setShowClientFinishDialog(true)}
                  className="w-full bg-[#3A4F6C] hover:bg-[#2A3B4F] text-white font-bold py-3"
                >
                  <CheckSquare className="h-5 w-5 mr-2" />
                  Marcar Trabajo Terminado
                </Button>
                <p className="text-xs text-slate-500 text-center mt-2">
                  {job.worker_id
                    ? 'Al cerrar se liberará el pago al trabajador (10% comisión)'
                    : 'Cierra este trabajo si ya no lo necesitas o lo resolviste por fuera'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Price Proposal Dialog */}
      <Dialog open={showPriceDialog} onOpenChange={setShowPriceDialog}>
        <DialogContent data-testid="price-dialog">
          <DialogHeader>
            <DialogTitle>Proponer Precio</DialogTitle>
            <DialogDescription>
              Ingresa el precio que cobrarás por este trabajo. El cliente decidirá si acepta tu oferta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-500 mb-2">Presupuesto del cliente:</p>
              <p className="text-xl font-bold text-slate-700 mb-4">${job.budget?.toLocaleString()} COP</p>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Tu precio (COP)</label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  data-testid="price-input"
                  type="number"
                  placeholder="Ej: 50000"
                  value={proposedPrice}
                  onChange={(e) => setProposedPrice(e.target.value)}
                  className="pl-10 text-lg"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="submit-price-button"
                onClick={handleProposePrice}
                disabled={proposingPrice || !proposedPrice}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold"
              >
                {proposingPrice ? 'Enviando...' : 'Enviar Propuesta'}
              </Button>
              <Button variant="outline" onClick={() => setShowPriceDialog(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finish Job (Worker) Dialog */}
      <Dialog open={showFinishDialog} onOpenChange={(open) => { setShowFinishDialog(open); if (!open) { setEvidenceFiles([]); setEvidenceComment(''); } }}>
        <DialogContent data-testid="finish-job-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Marcar Trabajo Terminado</DialogTitle>
            <DialogDescription>
              Sube entre 2 y 3 fotos como evidencia del trabajo finalizado. El cliente revisará y confirmará.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {evidenceFiles.map((f, i) => (
                <div key={i} className="relative">
                  <img
                    src={URL.createObjectURL(f)}
                    alt={`Evidencia ${i + 1}`}
                    className="w-full h-24 object-cover rounded border-2 border-slate-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeEvidence(i)}
                    data-testid={`remove-evidence-${i}`}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {evidenceFiles.length < 3 && (
                <button
                  type="button"
                  data-testid="add-evidence-button"
                  onClick={() => evidenceInputRef.current?.click()}
                  className="h-24 border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center text-slate-500 hover:border-orange-500 hover:text-orange-500 transition-colors"
                >
                  <ImagePlus className="h-6 w-6 mb-1" />
                  <span className="text-xs">Añadir</span>
                </button>
              )}
            </div>

            <input
              ref={evidenceInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={handleEvidenceSelect}
              data-testid="evidence-file-input"
            />

            <p className="text-xs text-slate-500">
              {evidenceFiles.length}/3 fotos seleccionadas (mínimo 2). Formatos: JPG, PNG, WebP · Máx 5MB c/u.
            </p>

            <div>
              <label className="text-sm font-semibold text-slate-700">Comentario (opcional)</label>
              <Textarea
                data-testid="evidence-comment"
                placeholder="Ej: Muro reparado y pintado. Listo para revisión."
                value={evidenceComment}
                onChange={(e) => setEvidenceComment(e.target.value)}
                className="mt-1"
                rows={3}
                maxLength={300}
              />
            </div>

            <div className="flex gap-2">
              <Button
                data-testid="submit-evidence-button"
                onClick={handleSubmitEvidence}
                disabled={submittingEvidence || evidenceFiles.length < 2}
                className="flex-1 bg-[#3A4F6C] hover:bg-[#2A3B4F] text-white font-bold"
              >
                {submittingEvidence ? 'Enviando...' : 'Enviar y Terminar'}
              </Button>
              <Button variant="outline" onClick={() => setShowFinishDialog(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Finish Dialog */}
      <Dialog open={showClientFinishDialog} onOpenChange={(open) => { setShowClientFinishDialog(open); if (!open) { setClientEvidenceFiles([]); setClientEvidenceComment(''); } }}>
        <DialogContent data-testid="client-finish-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Marcar Trabajo Terminado</DialogTitle>
            <DialogDescription>
              {job.worker_id
                ? `Vas a cerrar este trabajo y liberar el pago de $${(job.accepted_price || job.proposed_price || 0).toLocaleString()} COP al trabajador. Opcionalmente sube fotos de evidencia (0-3).`
                : 'Vas a cerrar este trabajo sin trabajador asignado. Opcionalmente sube fotos de evidencia (0-3).'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {clientEvidenceFiles.map((f, i) => (
                <div key={i} className="relative">
                  <img
                    src={URL.createObjectURL(f)}
                    alt={`Evidencia ${i + 1}`}
                    className="w-full h-24 object-cover rounded border-2 border-slate-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeClientEvidence(i)}
                    data-testid={`remove-client-evidence-${i}`}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {clientEvidenceFiles.length < 3 && (
                <button
                  type="button"
                  data-testid="add-client-evidence-button"
                  onClick={() => clientEvidenceInputRef.current?.click()}
                  className="h-24 border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center text-slate-500 hover:border-orange-500 hover:text-orange-500 transition-colors"
                >
                  <ImagePlus className="h-6 w-6 mb-1" />
                  <span className="text-xs">Añadir</span>
                </button>
              )}
            </div>

            <input
              ref={clientEvidenceInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={handleClientEvidenceSelect}
              data-testid="client-evidence-file-input"
            />

            <p className="text-xs text-slate-500">
              {clientEvidenceFiles.length}/3 fotos · opcionales · JPG, PNG, WebP · Máx 5MB c/u.
            </p>

            <div>
              <label className="text-sm font-semibold text-slate-700">Comentario (opcional)</label>
              <Textarea
                data-testid="client-evidence-comment"
                placeholder="Ej: Quedó perfecto. Gracias."
                value={clientEvidenceComment}
                onChange={(e) => setClientEvidenceComment(e.target.value)}
                className="mt-1"
                rows={3}
                maxLength={300}
              />
            </div>

            <div className="flex gap-2">
              <Button
                data-testid="submit-client-finish-button"
                onClick={handleClientFinish}
                disabled={submittingClientFinish}
                className="flex-1 bg-[#3A4F6C] hover:bg-[#2A3B4F] text-white font-bold"
              >
                {submittingClientFinish ? 'Procesando...' : 'Confirmar Terminación'}
              </Button>
              <Button variant="outline" onClick={() => setShowClientFinishDialog(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={showRatingDialog} onOpenChange={(open) => { setShowRatingDialog(open); if (!open) { setRatingValue(0); setRatingComment(''); } }}>
        <DialogContent data-testid="rating-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-orange-500" />
              ¿Cómo calificarías al trabajador?
            </DialogTitle>
            <DialogDescription>
              Tu calificación con ladrillitos ayuda a otros clientes a encontrar Socios Expertos de confianza.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="flex flex-col items-center gap-3">
              <BrickRatingInput
                value={ratingValue}
                onChange={setRatingValue}
                size="lg"
              />
              <p className="text-sm text-slate-500">
                {ratingValue === 0 && 'Toca los ladrillitos para calificar'}
                {ratingValue === 1 && 'Muy malo'}
                {ratingValue === 2 && 'Malo'}
                {ratingValue === 3 && 'Regular'}
                {ratingValue === 4 && 'Bueno'}
                {ratingValue === 5 && '¡Excelente!'}
              </p>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700">Comentario (opcional)</label>
              <Textarea
                data-testid="rating-comment"
                placeholder="Cuéntanos cómo fue el servicio..."
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                rows={3}
                maxLength={300}
                className="mt-1"
              />
            </div>

            <div className="flex gap-2">
              <Button
                data-testid="submit-rating-button"
                onClick={handleSubmitRating}
                disabled={submittingRating || ratingValue === 0}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold"
              >
                {submittingRating ? 'Enviando...' : 'Enviar Calificación'}
              </Button>
              <Button variant="outline" onClick={() => setShowRatingDialog(false)}>
                Después
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
        <DialogContent data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle>Reportar Problema</DialogTitle>
            <DialogDescription>
              Cuéntanos qué no está bien con el trabajo. Un administrador revisará el caso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              data-testid="dispute-reason"
              placeholder="Describe brevemente el problema..."
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <div className="flex gap-2">
              <Button
                data-testid="submit-dispute-button"
                onClick={handleReportIssue}
                disabled={submittingDispute || disputeReason.trim().length < 10}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold"
              >
                {submittingDispute ? 'Enviando...' : 'Enviar Reporte'}
              </Button>
              <Button variant="outline" onClick={() => setShowDisputeDialog(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
